import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Download, BarChart3, ShieldCheck, FlaskConical, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import DiscoverBanner from "../components/DiscoverBanner";

const reliability = [
  { label: "Test – Retest Method", value: "0.97" },
  { label: "Split Half Method", value: "0.74" },
  { label: "Correlation Coefficient (all groups)", value: "> 0.9" },
  { label: "Discrimination Index", value: "> 0.39" },
  { label: "Average Difficulty Index (P)", value: "0.45" },
];

const results = [
  "IQ – extremely statistically significant",
  "Focus – extremely statistically significant",
  "Decision Making Ability – extremely statistically significant",
  "Creative Quotient – extremely statistically significant",
  "Academic Marks – extremely statistically significant",
];

const papers = [
  {
    title: "Evidence of Effectiveness & Impact of Solutions",
    journal: "European Journal of Behavioral Sciences",
    href: "https://www.cracslab.com/_files/ugd/39404f_87efc3961d5a4e108d45ac9c7dded84d.pdf",
  },
  {
    title: "Impact of Solutions",
    journal: "International Conference on Advanced Research in Social Sciences, London",
    href: "https://www.cracslab.com/_files/ugd/39404f_1ff9d79b9eac47eea7df0c5095504efb.pdf",
  },
  {
    title: "Development & Standardization of Assessment (Nursery – Grade 2)",
    journal: "International Journal of Indian Psychology",
    href: "https://www.cracslab.com/_files/ugd/39404f_4bd974b2c5ad43a58f2816c32d636fb4.pdf",
  },
  {
    title: "Development of Cognitive Assessment (7 – 16 years)",
    journal: "International Journal of Applied Research",
    href: "https://www.cracslab.com/_files/ugd/39404f_6581d4d8b6044a628d55120acbaf5ae1.pdf",
  },
];

export default function Research() {
  return (
    <div className="overflow-hidden">
      <Seo
        title="Research, Validity & Standardization | MapMyKid"
        description="The validity, reliability and standardization of MapMyKid's cognitive assessments is proven and extremely statistically significant, backed by published research papers and rigorous methodology."
      />

      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>Research & Standardization</Eyebrow>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.06] text-slate-900">
            Proven. Published. <span className="brand-gradient-text">Statistically significant.</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Our assessments are built on a validated, standardized methodology. The results are documented in peer reviewed journals and international conferences.
          </p>
        </div>
      </section>

      {/* RELIABILITY */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Validity & Reliability" title="Numbers that stand up to scrutiny." subtitle="Across independent methods, our assessments demonstrate high reliability and strong statistical significance." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reliability.map((r, i) => (
              <Reveal key={r.label} delay={i * 70}>
                <div className="h-full rounded-3xl border border-violet-100 bg-white p-7 card-hover">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600"><BarChart3 className="h-6 w-6" /></span>
                  <p className="mt-4 text-3xl font-extrabold brand-gradient-text">{r.value}</p>
                  <p className="mt-1.5 text-base text-slate-600 leading-snug">{r.label}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={5 * 70}>
              <div className="h-full rounded-3xl cta-gradient p-7 text-white shadow-xl shadow-violet-300/40">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20"><FlaskConical className="h-6 w-6" /></span>
                <p className="mt-4 text-2xl font-extrabold">t = 30.6933</p>
                <p className="mt-1.5 text-base text-white/85 leading-snug">Two tailed, independent t-test. Extremely statistically significant evidence of effectiveness.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Evidence of Effectiveness" title="Significant gains across every dimension." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.map((t, i) => (
              <Reveal key={t} delay={(i % 2) * 80}>
                <div className="flex items-start gap-3 rounded-2xl border border-violet-100 bg-white p-5 card-hover">
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-teal-500" />
                  <span className="text-base font-medium text-slate-800">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PAPERS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Published Papers" title="Read the research for yourself." subtitle="A selection of the core papers documenting the development, standardization and effectiveness of the assessments." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {papers.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 90}>
                <a href={p.href} target="_blank" rel="noopener noreferrer" className="group flex h-full flex-col rounded-3xl border border-violet-100 bg-white p-6 sm:p-7 card-hover">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white"><FileText className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-lg font-bold text-slate-900 leading-snug">{p.title}</h3>
                  <p className="mt-2 flex-1 text-base text-slate-500">{p.journal}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-base font-semibold text-violet-700 group-hover:gap-2.5 transition-all">
                    <Download className="h-4.5 w-4.5" /> Download PDF
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          {/* Credit note */}
          <p className="mx-auto mt-10 max-w-3xl text-center text-slate-400 leading-relaxed" style={{ fontSize: "10px" }}>
            Research papers published and documented by Hemant Nanda and the Center for Research in Applied Cognitive Sciences (CRACS). MapMyKid's assessments are built upon this validated, standardized and statistically significant methodology. Only the primary papers with a publicly available download link are listed above.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[32px] border border-violet-100 bg-white p-8 sm:p-12 text-center shadow-xl shadow-violet-100/50">
              <ShieldCheck className="mx-auto h-10 w-10 text-violet-600" />
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900">A science you can trust.</h2>
              <p className="mx-auto mt-3 max-w-xl text-lg text-slate-600">See exactly what your child's cognitive profile can reveal.</p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
                <Link to="/assessments"><Button variant="outline" className="rounded-full h-14 px-7 text-base font-semibold border-violet-200 text-violet-700 hover:bg-violet-50 w-full sm:w-auto">Explore assessments</Button></Link>
                <Link to="/book"><Button className="cta-gradient text-white rounded-full h-14 px-7 text-base font-semibold w-full sm:w-auto">Book now <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <DiscoverBanner />
    </div>
  );
}
